import { useState, useCallback, useEffect, useRef } from 'react';
import { API_URL } from '../../../utils/api';
import { invalidateProcurementCache } from '../../../utils/cacheHelpers';
import { markCommentAsOK } from '../utils/feedbackUtils';

/**
 * Custom hook för att hantera feedback-actions (byggkonsult svar)
 *
 * @param {string} currentEstimatId - ID för aktuellt estimat
 * @param {Function} setSaving - Callback för att sätta sparstatus
 * @param {Object} notifications - NotificationContext API (från useNotifications hook)
 * @returns {Object} - State och funktioner för feedback-hantering
 */
const useFeedbackActions = (
  currentEstimatId,
  setSaving,
  notifications
) => {
  const [newByggkonsultResponses, setNewByggkonsultResponses] = useState({});
  const [savedByggkonsultResponses, setSavedByggkonsultResponses] = useState({});
  const [hasUnsavedResponses, setHasUnsavedResponses] = useState(false);
  const [showInlineComments, setShowInlineComments] = useState({});
  const [byggkonsultResponses, setByggkonsultResponses] = useState({});
  const initialDraftsSynced = useRef(false);

  // Synka befintliga utkast från databasen till savedByggkonsultResponses vid initial laddning
  useEffect(() => {
    if (initialDraftsSynced.current) return;
    const drafts = {};
    Object.entries(byggkonsultResponses).forEach(([key, val]) => {
      if (val && typeof val === 'object' && val.draft && val.text) {
        drafts[key] = val.text;
      }
    });
    if (Object.keys(drafts).length > 0) {
      setSavedByggkonsultResponses(prev => ({ ...prev, ...drafts }));
      // Populera även textfälten med utkasttext
      setNewByggkonsultResponses(prev => ({ ...prev, ...drafts }));
      initialDraftsSynced.current = true;
    }
  }, [byggkonsultResponses]);

  /**
   * Hantera ändring av byggkonsult-svar
   */
  const handleByggkonsultResponseChange = useCallback((commentKey, response) => {
    setNewByggkonsultResponses(prev => ({
      ...prev,
      [commentKey]: response
    }));
    setHasUnsavedResponses(true);
  }, []);

  /**
   * Markera kommentar som OK (inget svar behövs)
   */
  const handleMarkAsOK = useCallback(async (commentKey) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Använd generisk utility-funktion
      await markCommentAsOK(currentEstimatId, commentKey, 'consultant', token);
      
      // Uppdatera lokal state direkt för omedelbar UI-uppdatering
      setByggkonsultResponses(prev => ({
        ...prev,
        [commentKey]: '✓ Markerad som OK'
      }));
      
      // Invalidera cache
      await invalidateProcurementCache();
      
      // ✅ Använd globala notifications istället för modal
      notifications.showToast('Kommentar markerad som OK', 'success');
      
    } catch (error) {
      console.error('Fel vid markering som OK:', error);
      // ✅ Använd globala notifications istället för modal
      notifications.showError(
        'Kunde inte markera',
        'Ett fel uppstod när kommentaren skulle markeras som OK.'
      );
    } finally {
      setSaving(false);
    }
  }, [currentEstimatId, setSaving, notifications]);

  /**
   * Publicera alla utkast-svar (gör dem synliga för kund + skickar notifikationer)
   */
  const handleSendAllResponses = useCallback(async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      const res = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/publish-byggkonsult-responses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) throw new Error('Fel vid publicering av svar');
      const data = await res.json();

      // Uppdatera lokalt state
      if (data.estimat?.byggkonsultResponses) {
        setByggkonsultResponses(data.estimat.byggkonsultResponses);
      }

      const publishedCount = data.publishedCount || 0;
      setSavedByggkonsultResponses({});
      setNewByggkonsultResponses({});
      // Rensa unsaved-flaggan innan reload — annars triggar beforeunload-
      // listenern i EstimatPage browserns "Vill du läsa in webbplatsen igen?"-
      // dialog trots att vi just har sparat allt.
      setHasUnsavedResponses(false);

      await invalidateProcurementCache();

      notifications.showSuccess(
        'Alla svar skickade!',
        `${publishedCount} svar har skickats till kunden via e-post och de kan nu se dem i kundportalen.`,
        () => {
          window.location.reload();
        }
      );

    } catch (error) {
      console.error('Fel vid publicering av svar:', error);
      notifications.showError(
        'Fel vid skickande',
        'Ett fel uppstod när svaren skulle publiceras. Försök igen.'
      );
    } finally {
      setSaving(false);
    }
  }, [currentEstimatId, setSaving, notifications]);

  /**
   * Spara individuellt svar som utkast till backend (on blur)
   * Utkast syns inte för kund förrän BK klickar "Skicka svar"
   */
  const handleSaveByggkonsultResponse = useCallback(async (commentKey) => {
    const response = newByggkonsultResponses[commentKey];
    if (!response || !response.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/byggkonsult-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentKey, response: response.trim(), autoSave: true })
      });

      if (!res.ok) throw new Error('Kunde inte spara utkast');
      const data = await res.json();

      // Uppdatera lokalt state från backend-svar
      if (data.estimat?.byggkonsultResponses) {
        setByggkonsultResponses(data.estimat.byggkonsultResponses);
      }

      // Tracka som utkast för "Skicka svar"-knappen
      setSavedByggkonsultResponses(prev => ({
        ...prev,
        [commentKey]: response.trim()
      }));

      // Rensa textarean — utkastet visas i tråden istället. "Skicka svar"-knappen
      // styrs av savedByggkonsultResponses så den kvarstår.
      setNewByggkonsultResponses(prev => { const u = {...prev}; delete u[commentKey]; return u; });
    } catch (error) {
      console.error('Fel vid auto-save byggkonsult-svar:', error);
    }
  }, [currentEstimatId, newByggkonsultResponses]);

  /**
   * Ta bort ett BK-svar (draft) från backend
   */
  const handleDeleteByggkonsultResponse = useCallback(async (commentKey) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/byggkonsult-response`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ deleteResponseKeys: [commentKey], autoSave: true })
      });

      if (!res.ok) throw new Error('Kunde inte ta bort svar');
      const data = await res.json();

      if (data.estimat?.byggkonsultResponses) {
        setByggkonsultResponses(data.estimat.byggkonsultResponses);
      }

      // Rensa lokalt state
      setNewByggkonsultResponses(prev => { const u = {...prev}; delete u[commentKey]; return u; });
      setSavedByggkonsultResponses(prev => { const u = {...prev}; delete u[commentKey]; return u; });
    } catch (error) {
      console.error('Fel vid borttagning av BK-svar:', error);
    }
  }, [currentEstimatId]);

  /**
   * Redigera ett befintligt BK-svar (sätt tillbaka till redigeringsläge)
   */
  const handleEditByggkonsultResponse = useCallback((commentKey) => {
    const existing = byggkonsultResponses[commentKey];
    const text = existing && typeof existing === 'object' ? (existing.text || '') : (existing || '');
    setNewByggkonsultResponses(prev => ({ ...prev, [commentKey]: text }));
    // Ta bort från saved så att blur-save triggar korrekt
    setSavedByggkonsultResponses(prev => { const u = {...prev}; delete u[commentKey]; return u; });
  }, [byggkonsultResponses]);

  /**
   * Redigera en specifik thread-entry i BK:s svar (Slack-style edit av tidigare meddelanden)
   */
  const handleEditByggkonsultEntry = useCallback(async (commentKey, entryIndex, newText) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/estimat/${currentEstimatId}/byggkonsult-response/edit-entry`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentKey, entryIndex, newText })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Kunde inte redigera svar');
      }
      const data = await res.json();
      if (data.estimat?.byggkonsultResponses) {
        setByggkonsultResponses(data.estimat.byggkonsultResponses);
      }
    } catch (error) {
      console.error('Fel vid redigering av thread-entry:', error);
      notifications.showError('Kunde inte redigera', error.message);
    }
  }, [currentEstimatId, notifications]);

  /**
   * Kontrollera om svar har osparade ändringar
   */
  const hasUnsavedChangesInResponse = useCallback((commentKey) => {
    const currentResponse = newByggkonsultResponses[commentKey] || '';
    const savedResponse = savedByggkonsultResponses[commentKey] || '';
    return currentResponse.trim() !== savedResponse.trim() && currentResponse.trim() !== '';
  }, [newByggkonsultResponses, savedByggkonsultResponses]);

  /**
   * Visa/dölj inline kommentarer
   */
  const toggleInlineComment = useCallback((commentKey) => {
    setShowInlineComments(prev => ({
      ...prev,
      [commentKey]: !prev[commentKey]
    }));
  }, []);

  /**
   * Skicka alla svar till kund (alternativ metod) - publicerar alla utkast
   */
  const sendResponsestoCustomer = useCallback(async () => {
    // Kontrollera om det finns osparade ändringar (skrivna men inte sparade som utkast)
    const hasUnsaved = Object.keys(newByggkonsultResponses).some(key =>
      hasUnsavedChangesInResponse(key)
    );

    if (hasUnsaved) {
      const confirmed = await notifications.showConfirm(
        'Osparade kommentarer',
        'Du har osparade kommentarer som inte kommer att skickas. Vill du fortsätta ändå?'
      );
      if (!confirmed) {
        return;
      }
    }

    if (Object.keys(savedByggkonsultResponses).length === 0) {
      notifications.showInfo(
        'Inga svar att skicka',
        'Du måste först spara några svar innan du kan skicka dem till kunden.'
      );
      return;
    }

    // Använd publish-endpoint
    await handleSendAllResponses();
  }, [
    newByggkonsultResponses,
    savedByggkonsultResponses,
    hasUnsavedChangesInResponse,
    notifications,
    handleSendAllResponses
  ]);

  return {
    // State
    newByggkonsultResponses,
    savedByggkonsultResponses,
    hasUnsavedResponses,
    showInlineComments,
    byggkonsultResponses,
    
    // Setters (för initial data loading)
    setNewByggkonsultResponses,
    setSavedByggkonsultResponses,
    setHasUnsavedResponses,
    setShowInlineComments,
    setByggkonsultResponses,
    
    // Actions
    handleByggkonsultResponseChange,
    handleMarkAsOK,
    handleSendAllResponses,
    handleSaveByggkonsultResponse,
    handleDeleteByggkonsultResponse,
    handleEditByggkonsultResponse,
    handleEditByggkonsultEntry,
    hasUnsavedChangesInResponse,
    toggleInlineComment,
    sendResponsestoCustomer,
  };
};

export default useFeedbackActions;
